import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Button,
  styled
} from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useTranslation } from 'react-i18next';

// Ürün satış bilgisi arayüzü
interface ProductSales {
  itemId: string;
  itemName: string;
  totalSales: number;
}

interface BestSellingProductsWidgetProps {
  products: ProductSales[];
  loading: boolean;
  /** Başlık satırındaki "Detay" butonuna tıklanınca çağrılır (tüm liste modalı açılır) */
  onDetailClick?: () => void;
  /** Varsayılan "En Çok Satan Ürünler" başlığı yerine özel başlık (ekran bazlı set için) */
  title?: string;
  /** Başlık satırının sağına özel aksiyon (örn. ekran seçimi ikonu). Detay butonuyla birlikte gösterilir. */
  headerAction?: React.ReactNode;
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 8,
  overflow: 'hidden',
  '& .MuiCardHeader-root': {
    padding: theme.spacing(1),
    '& .MuiTypography-h6': {
      fontSize: '1rem',
      fontWeight: 'bold',
    },
  },
  '& .MuiCardContent-root': {
    padding: theme.spacing(1),
  },
}));

const BestSellingProductsWidget: React.FC<BestSellingProductsWidgetProps> = ({
  products,
  loading,
  onDetailClick,
  title,
  headerAction
}) => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'tr-TR';

  return (
    <StyledCard>
      <CardHeader 
        title={title ?? t('dashboard.bestSellingProductsWidgetTitle')}
        titleTypographyProps={{ fontWeight: 'bold' }}
        action={(onDetailClick || headerAction) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {onDetailClick && (
              <Button
                size="small"
                startIcon={<ListAltIcon />}
                onClick={onDetailClick}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                {t('dashboard.bestSellingProductsWidgetDetail')}
              </Button>
            )}
            {headerAction}
          </Box>
        ) : undefined}
      />
      <Divider />
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : products.length === 0 ? (
          <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
            {t('dashboard.bestSellingProductsWidgetNoData')}
          </Typography>
        ) : (
          <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper', width: 40, px: 1 }}>
                    {t('dashboard.rowNumber')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>
                    {t('dashboard.bestSellingProductsWidgetProductName')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>
                    {t('dashboard.bestSellingProductsWidgetSales')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product, index) => (
                  <TableRow key={product.itemId} hover>
                    <TableCell sx={{ width: 40, px: 1, fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontSize: '0.875rem',
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {product.itemName}
                    </TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontSize: '0.875rem',
                        color: 'primary.main',
                        fontWeight: 'medium'
                      }}
                    >
                      {product.totalSales.toLocaleString(locale, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                      })} {t('dashboard.piece')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default BestSellingProductsWidget;

